import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Chip,
  Grid2,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import { PowerSettingsNew, Add, Edit, Delete, Groups as GroupsIcon } from '@mui/icons-material';
import ButtonComponent from '../../../components/ButtonComponent';
import ModalComponent from '../../../components/ModalComponent';
import TextFieldComponent from '../../../components/TextFieldComponent';
import SwitchComponent from '../../../components/SwitchComponent';
import DropdownMultipleComponent from '../../../components/DropdownMultipleComponent';
import { AppContext } from '../../../context/AppContextProvider';
import {
  useGetAllDeviceGroupByLicenseAsync,
  useCreateDeviceGroupAsync,
  useUpdateDeviceGroupAsync,
  useDeleteDeviceGroupAsync,
} from '../../../services/DeviceGroupService/hooks/useMutation';
import { IDeviceGroupProps } from '../../../services/DeviceGroupService/types';
import { useGetAllHomeAssistantDevicesByLicenseAsync } from '../../../services/Devices/HomeAssistantDeviceService/hooks/useMutation';
import { useTriggerHomeAssistantGroupAsync } from '../../../services/Devices/HomeAssistantDeviceService/hooks/useMutation';
import { IHomeAssistantDeviceProps } from '../../../services/Devices/HomeAssistantDeviceService/types';
import ConfirmDeleteModalComponent from '../../../components/ConfirmDeleteModalComponent';

const GroupsTab = () => {
  const { showSnackbar } = useContext(AppContext);

  const [groups, setGroups] = useState<IDeviceGroupProps[]>([]);
  const [haDevices, setHaDevices] = useState<IHomeAssistantDeviceProps[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<IDeviceGroupProps>();
  const [name, setName] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<(string | number)[]>([]);
  const [triggeringGroupId, setTriggeringGroupId] = useState<number>();

  const { mutateAsync: mutateGetGroups, isLoading: isLoadingGroups } =
    useGetAllDeviceGroupByLicenseAsync();
  const { mutateAsync: mutateGetHaDevices } =
    useGetAllHomeAssistantDevicesByLicenseAsync();
  const { mutateAsync: mutateCreate } = useCreateDeviceGroupAsync();
  const { mutateAsync: mutateUpdate } = useUpdateDeviceGroupAsync();
  const { mutateAsync: mutateDelete } = useDeleteDeviceGroupAsync();
  const { mutateAsync: mutateTriggerGroup } = useTriggerHomeAssistantGroupAsync();

  const reload = () => {
    mutateGetGroups().then((result: any) => setGroups(result as IDeviceGroupProps[]));
    mutateGetHaDevices().then(setHaDevices);
  };

  useEffect(() => {
    reload();
  }, []);

  const resetForm = () => {
    setSelectedGroup(undefined);
    setName('');
    setIsActive(true);
    setSelectedDeviceIds([]);
    setIsModalOpen(false);
  };

  const handleOpenCreate = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const handleOpenEdit = (group: IDeviceGroupProps) => {
    setSelectedGroup(group);
    setName(group.name);
    setIsActive(group.isActive);
    setSelectedDeviceIds(group.devices.map((d) => d.id));
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    const data = {
      name,
      isActive,
      devices: selectedDeviceIds.map((id) => Number(id)),
    };
    if (selectedGroup) {
      await mutateUpdate({ id: selectedGroup.id, data });
      showSnackbar('Grupo atualizado com sucesso.', 'success');
    } else {
      await mutateCreate({ data });
      showSnackbar('Grupo criado com sucesso.', 'success');
    }
    resetForm();
    reload();
  };

  const handleConfirmDelete = async () => {
    if (!selectedGroup) return;
    await mutateDelete(selectedGroup.id);
    showSnackbar('Grupo removido com sucesso.', 'success');
    setIsDeleteOpen(false);
    setSelectedGroup(undefined);
    reload();
  };

  const handleTriggerGroup = async (
    group: IDeviceGroupProps,
    service: 'turn_on' | 'turn_off',
  ) => {
    setTriggeringGroupId(group.id);
    try {
      const results = await mutateTriggerGroup({ groupId: group.id, data: { service } });
      const failures = results.filter((r) => !r.success);
      showSnackbar(
        failures.length === 0
          ? `Grupo "${group.name}": todos os dispositivos acionados com sucesso.`
          : `Grupo "${group.name}": ${failures.length} de ${results.length} dispositivo(s) falharam.`,
        failures.length === 0 ? 'success' : 'error',
      );
    } finally {
      setTriggeringGroupId(undefined);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Grupos de dispositivos
        </Typography>
        <ButtonComponent variant="contained" startIcon={<Add />} onClick={handleOpenCreate}>
          Novo grupo
        </ButtonComponent>
      </Box>

      {isLoadingGroups ? (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Carregando grupos...
        </Typography>
      ) : groups.length === 0 ? (
        <Paper
          sx={{
            borderRadius: 4,
            p: 3,
            textAlign: 'center',
            color: 'text.secondary',
            border: `1px solid ${alpha('#ffffff', 0.1)}`,
            backgroundColor: alpha('#ffffff', 0.05),
          }}
        >
          Nenhum grupo cadastrado ainda.
        </Paper>
      ) : (
        <Grid2 container spacing={2}>
          {groups.map((group) => (
            <Grid2 key={group.id} size={{ xs: 12, sm: 6, lg: 4 }}>
              <Box
                sx={{
                  borderRadius: 4,
                  p: 2.5,
                  border: `1px solid ${alpha('#ffffff', 0.1)}`,
                  backgroundColor: alpha('#ffffff', 0.05),
                }}
              >
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 2 }}>
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: alpha('#a855f7', 0.15),
                        color: '#a855f7',
                        flexShrink: 0,
                      }}
                    >
                      <GroupsIcon />
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 700 }}>{group.name}</Typography>
                      <Chip
                        size="small"
                        label={group.isActive ? 'Ativo' : 'Inativo'}
                        color={group.isActive ? 'success' : 'default'}
                        sx={{ mt: 0.5 }}
                      />
                    </Box>
                  </Stack>
                  <Stack direction="row">
                    <Tooltip title="Editar">
                      <IconButton
                        size="small"
                        sx={{ color: '#fff' }}
                        onClick={() => handleOpenEdit(group)}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          setSelectedGroup(group);
                          setIsDeleteOpen(true);
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>

                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {group.devices?.length ?? 0} dispositivo(s)
                </Typography>

                <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                  <ButtonComponent
                    fullWidth
                    variant="contained"
                    color="success"
                    size="small"
                    startIcon={<PowerSettingsNew fontSize="small" />}
                    disabled={triggeringGroupId === group.id}
                    onClick={() => handleTriggerGroup(group, 'turn_on')}
                  >
                    Ativar
                  </ButtonComponent>
                  <ButtonComponent
                    fullWidth
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={<PowerSettingsNew fontSize="small" />}
                    disabled={triggeringGroupId === group.id}
                    onClick={() => handleTriggerGroup(group, 'turn_off')}
                  >
                    Desativar
                  </ButtonComponent>
                </Stack>
              </Box>
            </Grid2>
          ))}
        </Grid2>
      )}

      <ModalComponent
        isOpen={isModalOpen}
        onClose={resetForm}
        title={selectedGroup ? 'Editar grupo' : 'Novo grupo'}
      >
        <Box sx={{ minWidth: 360 }}>
          <TextFieldComponent
            label="Nome do grupo"
            value={name}
            onChangeText={setName}
            fullWidth
            sx={{ mb: 2 }}
          />
          <DropdownMultipleComponent<IHomeAssistantDeviceProps>
            label="Dispositivos Home Assistant"
            options={haDevices}
            value={selectedDeviceIds}
            onChange={setSelectedDeviceIds}
            labelField={'name'}
            valueField={'id'}
          />
          <Box sx={{ mt: 1, mb: 2 }}>
            <SwitchComponent
              label="Ativo"
              value={isActive}
              onChange={setIsActive}
              color="primary"
            />
          </Box>
          <ButtonComponent
            variant="contained"
            fullWidth
            disabled={!name}
            onClick={handleSave}
          >
            Salvar
          </ButtonComponent>
        </Box>
      </ModalComponent>

      <ConfirmDeleteModalComponent
        isVisibleDeleteDialog={isDeleteOpen}
        setIsVisibleDeleteDialog={setIsDeleteOpen}
        title="Excluir grupo"
        message={`Tem certeza que deseja excluir o grupo "${selectedGroup?.name}"?`}
        handleCancelDeletion={() => {
          setIsDeleteOpen(false);
          setSelectedGroup(undefined);
        }}
        handleConfirmDeletion={handleConfirmDelete}
      />
    </Box>
  );
};

export default GroupsTab;
